import { useStore } from "../store/globalState";
import {
  AuraLayout,
  CashIcon,
  KapitalIcon,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { memo, useEffect, useRef } from "react";
import { getEvents, safeCapture } from "../analytics";
import {
  clearFirstSendWarningCache,
  wasFirstSendWarningShown,
} from "../hooks/useFirstSendWarning";
import { clearPhoneSkip } from "../utils/phoneGate";
import { useTranslation } from "react-i18next";
import {
  APP_ROUTES,
  flavorClassName,
  isFlavorB,
} from "../constants";
import { FlowHeroMark } from "../components/FlowHeroMark";
import {
  amountFormatter,
  formatLabeledAmount,
  getCurrentDateFormatted,
} from "../utils";
import type { ITransaction } from "../interfaces/transactions";
import { TransactionsStatus } from "../utils/commons";
import { useSessionStore } from "../store/useSessionStore";
import {
  createOperation,
  getQuote,
  getAllPaymentMethods,
  getWidgetApiError,
  isDeadLinkError,
} from "../services/api";
import type {
  ICreateOperationRequest,
} from "../services/api";
import type { AxiosError } from "axios";
import type { IUserData } from "../shared/session/interfaces/session";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { resolveActiveRoute, FUNDING_METHOD_CATEGORY } from "../utils/catalog";
import type { FundingMethod } from "../utils/catalog";
import {
  createWithFreshQuote,
  waitForPersistHydration,
} from "../utils/quoteCommit";

/**
 * Dead-link errors (LINK_NOT_FOUND / 404, expired, revoked) are handled
 * globally by the dead-link event handler, so we must not override them here.
 */
const isSessionExpiredError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  const errorCode = axiosError?.response?.data?.error?.code;
  return (
    isDeadLinkError(err) ||
    errorCode === "LINK_NOT_FOUND" ||
    axiosError?.response?.status === 404
  );
};

/**
 * Business-case recipe of the m2m flow: the recipient IS the sender
 * (self-send), so the recipient party is built from the claim's `userData`.
 * Prefers the structured `name` when the partner provided it; degrades to a
 * positional split of `fullName` (first token → firstName, rest → lastName).
 * Returns `undefined` when the claim carries no usable name — the operation
 * is then created without a recipient party (same as before this recipe
 * existed; the gap surfaces via the backend's completeness event).
 */
/** Claim links use partner `secondName`; operations API expects `middleName`. */
const toCanonicalRecipientName = (
  name: NonNullable<IUserData["name"]> & {
    secondName?: string;
    middleName?: string;
    secondLastName?: string;
  },
): NonNullable<ICreateOperationRequest["recipient"]>["name"] => ({
  firstName: name.firstName,
  lastName: name.lastName,
  ...(name.middleName ?? name.secondName
    ? { middleName: name.middleName ?? name.secondName }
    : {}),
  ...(name.secondLastName ? { secondLastName: name.secondLastName } : {}),
});

const buildRecipientFromClaim = (
  userData: IUserData | undefined,
): ICreateOperationRequest["recipient"] => {
  if (userData?.name?.firstName && userData?.name?.lastName) {
    return { name: toCanonicalRecipientName(userData.name) };
  }
  const tokens = userData?.fullName?.trim().split(/\s+/) ?? [];
  if (tokens.length >= 2) {
    return {
      name: {
        firstName: tokens[0],
        lastName: tokens.slice(1).join(" "),
      },
    };
  }
  return undefined;
};

/**
 * Module-level flag to prevent duplicate operation creation.
 * Unlike useRef, this survives component unmount/remount cycles
 * (StrictMode, routing re-mounts, etc.) so only ONE executeOperation
 * can ever be in-flight at a time. Reset when the operation finishes
 * (success or error) so future navigations to this page work normally.
 */
let isOperationInFlight = false;

const MIN_TRANSFER_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_TRANSFER_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

export const SendingMoneyPage = memo(() => {
  const { t } = useTranslation();
  const { theme, setTransactions, quoteState, setQuoteState } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, dropzoneColors, textColorBtn, title } = colorConfig;
  const { redirectTo } = useRouteWithToken();
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    // Module-level guard: prevents duplicate execution from StrictMode
    // double-effects, routing re-mounts, or any other scenario that
    // causes the effect to fire more than once.
    if (isOperationInFlight) {
      console.log("[SendingMoneyPage] Skipping - operation already in flight");
      return;
    }
    isOperationInFlight = true;

    const handleOperationSuccess = (
      operation: Awaited<ReturnType<typeof createOperation>>,
      quote: {
        fundingMethod?: string;
        payoutMethod?: string;
        capabilityKey?: string;
      },
    ) => {
      safeCapture(getEvents().OPERATION_CREATED, {
        success: true,
        first_send_warning_shown: wasFirstSendWarningShown(),
        operation_id: operation.operationId,
        quote_id: operation.quoteId,
        status: operation.status,
        send_amount: operation.sendAmount,
        receive_amount: operation.receiveAmount,
        exchange_rate: operation.exchangeRate,
        total_cost: operation.totalCost,
        funding_method: quote.fundingMethod,
        payout_method: quote.payoutMethod,
        capability_key: quote.capabilityKey,
      });
      clearFirstSendWarningCache();
      if (session.data?.linkId) clearPhoneSkip(session.data.linkId);
      const { transactions: txList, operations, setOperations } =
        useStore.getState();

      const newTransaction: ITransaction = {
        operationId: operation.operationId,
        status:
          operation.status === "pending"
            ? TransactionsStatus.PENDING
            : TransactionsStatus.COMPLETED,
        amount: amountFormatter(operation.receiveAmount),
        currency: operation.destinationCurrency,
        date: getCurrentDateFormatted(),
      };

      setTransactions([...txList, newTransaction]);

      const opIdx = operations.findIndex(
        (o) => o.operationId === operation.operationId
      );
      if (opIdx >= 0) {
        const next = [...operations];
        next[opIdx] = operation;
        setOperations(next);
      } else {
        setOperations([operation, ...operations]);
      }
    };

    const executeOperation = async () => {
      try {
        await waitForPersistHydration(useStore.persist);
        const capturedQuoteState = useStore.getState().quoteState;
        const linkId = session.data?.linkId;
        const userStatus = session.data?.userStatus;
        const canSend = !userStatus?.blocked && !userStatus?.suspended;
        if (!canSend) {
          redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
          return;
        }
        const signedQuote = capturedQuoteState?.signedQuote;

        const fundingMethod = capturedQuoteState?.fundingMethod as
          | FundingMethod
          | undefined;
        const category = fundingMethod
          ? FUNDING_METHOD_CATEGORY[fundingMethod]
          : undefined;

        const fundingSkipsInstrument = category === "CASH";

        let fundingPaymentMethodId: string | undefined;

        if (!fundingSkipsInstrument && linkId) {
          try {
            const paymentMethods = await getAllPaymentMethods(linkId);
            if (category === "CARD") {
              const card =
                paymentMethods.cards.find((c) => c.isDefault && c.enabled) ||
                paymentMethods.cards.find((c) => c.enabled);
              fundingPaymentMethodId = card?.paymentMethodId;
            } else if (category === "ACCOUNT") {
              const ba =
                paymentMethods.bankAccounts.find(
                  (b) => b.isDefault && b.enabled
                ) || paymentMethods.bankAccounts.find((b) => b.enabled);
              fundingPaymentMethodId = ba?.paymentMethodId;
            }
          } catch (pmErr) {
            console.error(
              "[SendingMoneyPage] Failed to fetch payment methods:",
              pmErr
            );
          }
        }

        if (!fundingSkipsInstrument && !fundingPaymentMethodId) {
          const { paymentState } = useStore.getState();
          if (paymentState.payment?.cardId) {
            fundingPaymentMethodId = paymentState.payment.cardId;
          }
        }

        if (!linkId) {
          throw new Error("No linkId found in session");
        }
        if (!signedQuote || !capturedQuoteState) {
          throw new Error("No signed quote available");
        }
        if (!fundingSkipsInstrument && !fundingPaymentMethodId) {
          throw new Error("No funding payment method selected");
        }

        const recipient = buildRecipientFromClaim(session.data?.userData);
        const route = resolveActiveRoute(
          session.data?.config,
          session.data?.userData,
        );

        const { result: operation, quote: usedQuote } =
          await createWithFreshQuote({
            quote: capturedQuoteState,
            route: {
              sourceCountry: route.sourceCountry,
              destinationCountry: route.destinationCountry,
              sourceCurrency: route.sourceCurrency,
            },
            getQuote,
            create: (freshSignedQuote) =>
              createOperation(linkId, {
                signedQuote: freshSignedQuote,
                ...(fundingPaymentMethodId
                  ? { fundingPaymentMethodId }
                  : {}),
                ...(recipient ? { recipient } : {}),
              }),
          });
        setQuoteState(usedQuote);
        handleOperationSuccess(operation, usedQuote);
        await holdMinimumVisible(visibleSinceRef.current);
        redirectTo(
          `${APP_ROUTES.RECEIPT_SUMMARY_PAGE.replace(":operationId", operation.operationId)}`,
          { replace: true },
        );
      } catch (err) {
        const axiosError = err as AxiosError<{
          success?: boolean;
          error?: { code?: string; message?: string };
        }>;
        const serverError = axiosError?.response?.data?.error;

        console.error("[SendingMoneyPage] Failed to create operation:", {
          message: axiosError?.message,
          status: axiosError?.response?.status,
          serverErrorCode: serverError?.code,
          serverErrorMessage: serverError?.message,
          fullResponse: axiosError?.response?.data,
        });

        if (isSessionExpiredError(err)) {
          return;
        }

        const apiCode = getWidgetApiError(err)?.code;
        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          redirectTo(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.transfersDisabledTitle"),
              subTitle: null,
              message: t("error.transfersDisabledMessage"),
              btnText: t("common.needHelp"),
              isOut: false,
              pageUrl: APP_ROUTES.SUPPORT_PAGE,
            },
          });
          return;
        }

        safeCapture(getEvents().OPERATION_CREATED, {
          success: false,
          first_send_warning_shown: wasFirstSendWarningShown(),
          error: err instanceof Error ? err.message : "Create operation failed",
          error_code: "OPERATION_CREATE_FAILED",
        });
        clearFirstSendWarningCache();
      if (session.data?.linkId) clearPhoneSkip(session.data.linkId);
        redirectTo(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      } finally {
        isOperationInFlight = false;
      }
    };

    executeOperation();

    // No cleanup function — we WANT the operation to run to completion
    // even if React unmounts/remounts this component (StrictMode, routing).
    // The module-level isOperationInFlight flag prevents duplicates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use quoteState.sendAmount for display — it IS persisted to localStorage
  // (amountState is NOT persisted and resets to "" after CIP redirect, causing NaN)
  const displayAmount = quoteState?.sendAmount ?? 0;
  const displayCurrency = quoteState?.sourceCurrency ?? "USD";

  const contentAlignClassName = isFlavorB ? "content-center" : "";
  const content = isFlavorB ? (
    <section className="sending-money-container">
      <KapitalIcon
        size={120}
        colors={dropzoneColors}
        className="animate-spin bg-transparent"
      />
      <section className="sending-caption">
        <PageTitle
          highlight={t("transfer.transferring")}
          highlightColor={title}
          secudnary={formatLabeledAmount(displayAmount, displayCurrency)}
          secudnaryColor={title}
        />
      </section>
    </section>
  ) : (
    <section className="sending-money-container">
      <FlowHeroMark
        showSpinner
        dropzoneColors={dropzoneColors}
        backgroundIcon={backgroundBtn}
      >
        <CashIcon size={60} colors={[textColorBtn, textColorBtn]} />
      </FlowHeroMark>
      <section className="sending-caption">
        <h1 className="foreground highlight text-centered">
          {t("transfer.transferring")}
          <span className="span-block lola-title-h1 h1">
            {formatLabeledAmount(displayAmount, displayCurrency)}
          </span>
        </h1>
      </section>
    </section>
  );

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`white-view-background ${flavorClassName}`}
    >
      <Layout.Content
        className={`loading-section sending-money-section ${contentAlignClassName}`}
      >
        {content}
      </Layout.Content>
    </AuraLayout>
  );
});